import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import * as echarts from 'echarts';
import { useTranslate } from 'react-admin';

// ---------------------------------------------------------------------------
// Types matching backend DTOs at:
//   apps/bff-tenant-api/src/job-application/dto/dashboard/
// ---------------------------------------------------------------------------

export interface ChartDataPoint {
  name: string;
  value: number;
}

export interface TimelineDataPoint {
  date: string;
  count: number;
  cumulative: number;
}

export interface SourcingDashboardData {
  total: number;
  contacted: number;
  contactRate: string;
  bySource: ChartDataPoint[];
  byCity: ChartDataPoint[];
  byCompany: ChartDataPoint[];
  timeline: TimelineDataPoint[];
}

export interface FunnelDataPoint {
  name: string;
  value: number;
  conversionRate: string;
}

export interface StatusTiming {
  statusId: number;
  statusName: string;
  averageDays: number;
  minDays: number;
  maxDays: number;
  totalDays: number;
  count: number;
}

export interface ClosureBreakdown {
  reasonName: string;
  category: string;
  phase: string;
  count: number;
}

export interface ConversionDashboardData {
  funnel: FunnelDataPoint[];
  closureReasons: ClosureBreakdown[];
  exitedFromStatus: ChartDataPoint[];
  statusTimings: StatusTiming[];
  /** Analytics cutover (ISO). Null when no cutover is configured. */
  dataSinceAt: string | null;
}

export interface RetentionCheckpoint {
  days: number;
  activeCount: number;
  /** Cohort apps that have reached this checkpoint (denominator). */
  maturedCount: number;
  /** Numeric with '%' (e.g. "82.5%") or sentinel "Em andamento". */
  retentionRate: string;
}

export interface CohortRow {
  cohort: string;
  totalHired: number;
  checkpoints: RetentionCheckpoint[];
}

export interface RetentionDashboardData {
  activeNow: number;
  /** Current employees = hired (post-cutover) and not terminated. */
  activeHeadcount: number;
  /** Tenure (days) distribution over current employees. */
  tenureActivesAvgDays: number;
  tenureActivesMedianDays: number;
  tenureActivesP90Days: number;
  /** Signed but vanished before becoming active. */
  onboardingGhostCount: number;
  /** Apps that ever reached "Contratado" (signed). */
  dealClosedCount: number;
  /** dealClosedCount - activeHeadcount. */
  dealClosedVsActiveGap: number;
  terminatedInPeriod: number;
  /** Hired headcount active at period start. Context only, not the denominator. */
  headcountStart: number;
  /** Turnover denominator: everyone who held a job at any point in the period. */
  turnoverBase: number;
  /**
   * Numeric with '%' (e.g. "4.2%"), or null when turnoverBase is 0 — there
   * is no measurable base, which is NOT the same as a measured 0%.
   */
  turnoverRate: string | null;
  avgDaysAsHiredBeforeTerminate: number;
  cohorts: CohortRow[];
  postHireTerminationReasons: ChartDataPoint[];
  /**
   * Post-hire turnover by closure category. `name` is the raw enum value
   * (candidate_decision/company_decision/candidate_dropout); the FE maps it
   * to a friendly label via i18n.
   */
  turnoverByCategory: ChartDataPoint[];
  /** Analytics cutover (ISO). Null when no cutover is configured. */
  dataSinceAt: string | null;
}

export interface AgingBucket {
  bucket: string;
  count: number;
}

export interface StatusAging {
  statusId: number;
  statusName: string;
  total: number;
  buckets: AgingBucket[];
}

export interface StaleApplication {
  jobApplicationId: number;
  candidateName: string;
  statusId: number;
  statusName: string;
  daysInStatus: number;
}

export interface OperationsDashboardData {
  aging: StatusAging[];
  staleApplications: StaleApplication[];
  /** Analytics cutover (ISO). Null when no cutover is configured. */
  dataSinceAt: string | null;
}

export interface OverviewDashboardData {
  newInPeriod: number;
  hiredInPeriod: number;
  activeNow: number;
  turnoverInPeriod: number;
  stuckOver7Days: number;
  totalInPipeline: number;
  periodStart: string;
  periodEnd: string;
  /** Analytics cutover (ISO). Null when no cutover is configured. */
  dataSinceAt: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a YYYY-MM-DD string into an ISO string covering the full day */
export const toBrowserDayIsoRange = (
  dateStr: string | null | undefined,
  isEnd: boolean,
): string | null => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(
    year,
    month - 1,
    day,
    isEnd ? 23 : 0,
    isEnd ? 59 : 0,
    isEnd ? 59 : 0,
    isEnd ? 999 : 0,
  );
  return date.toISOString();
};

/** Format an ISO datetime as DD/MM/AAAA (browser-local). */
export const formatCutoverDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
};

/** Convert an ISO datetime into a YYYY-MM-DD string (browser-local). */
export const isoToDateInputValue = (
  iso: string | null | undefined,
): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Default date range: last 90 days (start) to today (end), as YYYY-MM-DD */
export const useDefaultDateRange = () => {
  const ninetyDaysAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date.toISOString().split('T')[0];
  }, []);
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  return { ninetyDaysAgo, today };
};

// ---------------------------------------------------------------------------
// Reusable UI bits
// ---------------------------------------------------------------------------

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
  loading?: boolean;
  tooltip?: string;
}

export const StatCard = ({
  title,
  value,
  icon: Icon,
  color,
  loading,
  tooltip,
}: StatCardProps) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.15)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.3)}`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(color, 0.25)}`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem',
                }}
              >
                {title}
              </Typography>
              {tooltip && (
                <Tooltip title={tooltip} arrow placement="top">
                  <InfoIcon
                    sx={{
                      fontSize: 15,
                      color: theme.palette.text.disabled,
                      cursor: 'help',
                      '&:hover': { color: theme.palette.primary.main },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            {loading ? (
              <Skeleton width={80} height={48} />
            ) : (
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color,
                  lineHeight: 1,
                }}
              >
                {value}
              </Typography>
            )}
          </Box>
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: alpha(color, 0.15) }}>
            <Icon sx={{ fontSize: 28, color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

interface ChartCardProps {
  title: string;
  tooltip?: string;
  children: React.ReactNode;
}

export const ChartCard = ({ title, tooltip, children }: ChartCardProps) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        '&:hover': { boxShadow: theme.shadows[4] },
        transition: 'box-shadow 0.3s ease',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {title}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <IconButton
                size="small"
                sx={{
                  p: 0.5,
                  color: theme.palette.text.secondary,
                  '&:hover': { color: theme.palette.primary.main },
                }}
              >
                <InfoIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
};

interface EChartsComponentProps {
  option: echarts.EChartsOption;
  height?: number;
  loading?: boolean;
}

export const EChartsComponent = ({ option, height = 350, loading = false }: EChartsComponentProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const theme = useTheme();
  const [isChartReady, setIsChartReady] = useState(false);

  useEffect(() => {
    if (!chartRef.current || loading) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    const initTimer = setTimeout(() => {
      if (!chartRef.current) return;
      chartInstance.current = echarts.init(chartRef.current, theme.palette.mode);
      setIsChartReady(true);
      setTimeout(() => {
        chartInstance.current?.resize();
      }, 50);
    }, 0);

    const resizeObserver = new ResizeObserver(() => {
      chartInstance.current?.resize();
    });
    resizeObserver.observe(chartRef.current);

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
      setIsChartReady(false);
    };
  }, [theme.palette.mode, loading]);

  useEffect(() => {
    if (!chartInstance.current || !isChartReady || loading) return;
    chartInstance.current.setOption(option, true);
    requestAnimationFrame(() => {
      chartInstance.current?.resize();
    });
  }, [option, isChartReady, loading]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Skeleton variant="rectangular" width="100%" height={height - 40} sx={{ borderRadius: 2 }} />
        </Box>
      )}
      <div
        ref={chartRef}
        style={{
          width: '100%',
          height: '100%',
          visibility: loading ? 'hidden' : 'visible',
        }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Date range filter (used by Sourcing/Conversion/Retention dashboards)
// ---------------------------------------------------------------------------

interface DateRangeFilterProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  /**
   * Earliest selectable date (YYYY-MM-DD), e.g. the analytics cutover. When
   * set, the date-pickers clamp to it since earlier records aren't counted.
   */
  minDate?: string;
}

export const DateRangeFilter = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
}: DateRangeFilterProps) => {
  const translate = useTranslate();
  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            type="date"
            fullWidth
            size="small"
            label={translate('resources.job-application-dashboard.filters.startDate')}
            InputLabelProps={{ shrink: true }}
            inputProps={minDate ? { min: minDate } : undefined}
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            type="date"
            fullWidth
            size="small"
            label={translate('resources.job-application-dashboard.filters.endDate')}
            InputLabelProps={{ shrink: true }}
            inputProps={minDate ? { min: minDate } : undefined}
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Dashboard page header
// ---------------------------------------------------------------------------

interface DashboardHeaderProps {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  /**
   * Analytics cutover (ISO) echoed by the backend DTOs. When set, a chip is
   * rendered explaining that records before this date aren't counted.
   */
  dataSinceAt?: string | null;
}

export const DashboardHeader = ({
  title,
  subtitle,
  children,
  dataSinceAt,
}: DashboardHeaderProps) => {
  const theme = useTheme();
  const translate = useTranslate();
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 1,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body1" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      {dataSinceAt && (
        <Chip
          icon={<HistoryToggleOffIcon />}
          size="small"
          variant="outlined"
          color="info"
          sx={{ mt: 1 }}
          label={translate('resources.job-application-dashboard.dataSince', {
            date: formatCutoverDate(dataSinceAt),
          })}
        />
      )}
      {children}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Standard chart color palettes
// ---------------------------------------------------------------------------

export const CHART_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#3b82f6',
];
